import { createContext, useContext, useMemo } from 'react';
import type { PropsWithChildren } from 'react';

import useStudentDashboardCourseProgresses, {
  CourseProgressSelection,
} from '@/data/hook/student-dashboard/useStudentDashboardCourseProgresses';
import useAuth from '@/data/hook/useAuth';
import { InfinitePagination } from '@/data/hook/useInfiniteService';
import { isStudent } from '@/functions/auth';
import StudentDashboardStats from '@/models/StudentDashboard';

export type StudentDashboardContextValue = InfinitePagination & {
  courseProgresses: StudentDashboardStats[];
  activeCourseProgress?: StudentDashboardStats;
  selectCourseProgress(courseProgressId: number): void;
  isLoading: boolean;
  error: unknown;
};

const StudentDashboardContext =
  createContext<StudentDashboardContextValue | null>(null);

type StudentDashboardProviderProps = PropsWithChildren<{
  selection: CourseProgressSelection;
}>;

export function StudentDashboardProvider({
  children,
  selection,
}: StudentDashboardProviderProps) {
  const { user } = useAuth();
  const studentId = user && isStudent(user.userType) ? user.id : undefined;
  const {
    courseProgresses,
    activeCourseProgress,
    selectCourseProgress,
    isLoading,
    error,
    hasNextPage,
    isFetchingNextPage,
    fetchNextPage,
  } = useStudentDashboardCourseProgresses({ studentId, selection });

  const value = useMemo(
    () => ({
      courseProgresses,
      activeCourseProgress,
      selectCourseProgress,
      isLoading,
      error,
      hasNextPage,
      isFetchingNextPage,
      fetchNextPage,
    }),
    [
      courseProgresses,
      activeCourseProgress,
      selectCourseProgress,
      isLoading,
      error,
      hasNextPage,
      isFetchingNextPage,
      fetchNextPage,
    ],
  );

  return (
    <StudentDashboardContext.Provider value={value}>
      {children}
    </StudentDashboardContext.Provider>
  );
}

export function useStudentDashboard() {
  const context = useContext(StudentDashboardContext);
  if (!context) {
    throw new Error(
      'useStudentDashboard must be used within a StudentDashboardProvider',
    );
  }
  return context;
}
